import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AddUniversal,
  MoreInfoOutlined,
  SubtractUniversal,
  WarningAltOutlined,
} from '@duxton-web/icons';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Input,
  notification,
  Select,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import dayjs from 'dayjs';
import { produce } from 'immer';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';
import { usePrevious } from 'react-use';

import Overlay from '../../components/overlay';
import {
  BLACKLISTED_GUARDRAIL_METRICS,
  ControlMetricWithFilters,
  ExperimentAnalysisConfig,
  ExperimentAssignmentStrategy,
  getAssignByFacetFromDomain,
  getAssignmentStrategy,
  isCreateMode,
  isEditMode,
  isTimeSliceStrategy,
  isViewMode,
  Metric,
  MetricWithDimensionFields,
} from '../../constants';
import {
  useExperimentContext,
  useExperimentDispatchContext,
} from '../../contexts/experiment-context';
import {
  updateAnalysisConfig,
  updateExperiment,
  updateValidMetric,
} from '../../store/actions/experiment/action-creators';
import {
  AXA_INCOMPATIBLE_OPT_OUT_REASON,
  constructDimensionFields,
  isEligibleForPostEndEditing,
  // TEMP: Remove once temporary timeslice controlled metrics editing is no longer needed.
  isTimesliceEligibleForControlledMetricsEditing,
  isTimesliceExperiment,
  isValidMetric,
} from '../../utils';
import {
  isDimensionFilterValueEmpty,
  isFreeTextFilterPendingValidation,
} from '../../utils/dimension-filter';
import ControlledMetricsSection from './controlled-metrics';
import { MetricsWrapper } from './index.styled';
import MetricRow from './metric-row';
import { processAllMetricTypesForPopulationFilters } from './population-filter-utils';
import { renderPopulationFilterBadges } from './population-filters';
import {
  GRABX_SLACK_CHANNEL,
  HTTP_STATUS,
  MIDAS_METRICS_ENDPOINT,
  REQUESTED,
  RUNNING,
  SCHEDULED,
  STARTED,
} from '@/constants';
import Service from '@/lib/App';
import SectionCollapse from '@/pages/ExperimentFormV2/common/SectionCollapse';
import { generateMetricMessage } from '@/pages/ExperimentFormV2/helper/metric';
import {
  formatUTCStringToDateTimeString,
  getTimezoneDisplay,
} from '@/utils/dateTime';
import * as logger from '@/utils/logging';
import { isAdmin } from '@/utils/user';

const { Option } = Select;

const { Text } = Typography;

// Debounce delay for metric search inputs (ms)
const METRIC_SEARCH_DEBOUNCE_MS = 500;

const errorLimitMsg = (type: string, n: number) => {
  return `Maximum limit of ${n} ${type} metrics!`;
};

const DEFAULT_DEEP_DIVE_ALL_DIMENSIONS = true;
const DEFAULT_ENABLE_TRIGGERED_ANALYSIS = false;
const MAX_SECONDARY_METRICS = 10;
const MAX_GUARDRAIL_METRICS = 6;
const METRICS_UNSUPPORTED_AUTO_OPT_OUT_REASON =
  'AXA does not yet support metrics for this assignment strategy';

// check if should clear metrics when strategy changes
const isCompatibleStrategy = (
  strategy1: string,
  strategy2: string,
): boolean => {
  if (strategy1 === strategy2) return true;

  const choiceStrategies = [
    ExperimentAssignmentStrategy.UNIFORM_CHOICE,
    ExperimentAssignmentStrategy.WEIGHTED_CHOICE,
  ];

  return (
    choiceStrategies.includes(strategy1) && choiceStrategies.includes(strategy2)
  );
};

export const getOptions = (data: Metric[], targetMetrics: Metric[]): any[] => {
  const finalData = data?.length > 0 ? data : targetMetrics;
  return finalData?.map((metric: Metric) => {
    return (
      <Option
        key={metric.metadata?.display_name || metric.name}
        label={
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Text>{metric?.metadata?.display_name || metric.name}</Text>
            {metric?.version && (
              <Tag size="xsmall" style={{ height: 'fit-content' }}>
                {`v${metric.version}`}
              </Tag>
            )}
            {metric?.is_local_timezone && (
              <Tooltip
                title={
                  <>
                    Results on the experiment&apos;s first and last day may be
                    slightly inaccurate because its a local timezone metric.{' '}
                    <a
                      href="https://docs.google.com/document/d/155iAwQKnvqCHpZE2ScsLU1oukepQrHt0xHaBq7pvgPM/edit?tab=t.0#bookmark=id.eiutk2a98vgc"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Know More
                    </a>
                  </>
                }
              >
                <WarningAltOutlined
                  style={{
                    color: '#faad14',
                    cursor: 'help',
                  }}
                />
              </Tooltip>
            )}
          </div>
        }
        value={metric.name}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text>{metric?.metadata?.display_name}</Text>
              {metric?.version && (
                <Tag size="xsmall" style={{ height: 'fit-content' }}>
                  {`v${metric.version}`}
                </Tag>
              )}
              {metric?.is_local_timezone && (
                <Tooltip
                  title={
                    <>
                      Results on the experiment&apos;s first and last day may be
                      slightly inaccurate because its a local timezone metric.{' '}
                      <a
                        href="https://docs.google.com/document/d/155iAwQKnvqCHpZE2ScsLU1oukepQrHt0xHaBq7pvgPM/edit?tab=t.0#bookmark=id.eiutk2a98vgc"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Know More
                      </a>
                    </>
                  }
                >
                  <WarningAltOutlined
                    style={{
                      color: '#faad14',
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              )}
            </div>
            <Tooltip
              color="#FFF"
              zIndex={9999}
              overlayStyle={{ minWidth: '320px' }}
              title={
                <div style={{ padding: '12px 8px 8px 8px' }}>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        padding: 8,
                        background: '#FFF4EB',
                        border: '1px solid #F09800',
                        borderRadius: 8,
                      }}
                    >
                      <Text style={{ color: '#000', fontSize: 12 }}>
                        This metric was last refreshed on{' '}
                        {formatUTCStringToDateTimeString(
                          metric?.metric_quality?.last_refresh_date,
                        )}{' '}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {getTimezoneDisplay()}
                      </Text>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text style={{ fontSize: 16 }} strong>
                        {metric?.metadata?.display_name}
                      </Text>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text strong style={{ fontSize: 12 }} type="secondary">
                        DESCRIPTION
                      </Text>
                      <Text style={{ color: '#000', fontSize: 12 }}>
                        {metric.description || '-'}
                      </Text>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text strong style={{ fontSize: 12 }} type="secondary">
                        OWNER
                      </Text>
                      {metric.owners?.length ? (
                        metric.owners.map((owner) => (
                          <Text key={owner} style={{ fontSize: 12 }}>
                            @{owner}
                          </Text>
                        ))
                      ) : (
                        <Text style={{ fontSize: 12 }} type="secondary">
                          -
                        </Text>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text strong style={{ fontSize: 12 }} type="secondary">
                        TABLE
                      </Text>
                      {metric.sources?.length ? (
                        metric.sources.map((s) => (
                          <Text key={s} style={{ fontSize: 12 }}>
                            {s}
                          </Text>
                        ))
                      ) : (
                        <Text style={{ fontSize: 12 }} type="secondary">
                          -
                        </Text>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text strong style={{ fontSize: 12 }} type="secondary">
                        NUMERATOR
                      </Text>
                      <Text style={{ color: '#000', fontSize: 12 }}>
                        {metric?.expr || '-'}
                      </Text>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text strong style={{ fontSize: 12 }} type="secondary">
                        WHERE
                      </Text>
                      <SubtractUniversal style={{ color: '#000' }} />
                    </div>
                  </div>
                </div>
              }
            >
              <MoreInfoOutlined style={{ color: '#4096ff' }} />
            </Tooltip>
            <br />
            <div style={{ display: 'flex', alignItems: 'top' }}>
              {metric.owners?.length &&
                metric.owners.map((owner) => (
                  <>
                    <Text key={owner} style={{ fontSize: 12 }}>
                      @{owner}
                    </Text>
                    <span style={{ margin: '0 2px' }} />
                  </>
                ))}

              {metric.description && (
                <Text
                  style={{ whiteSpace: 'normal', fontSize: 12 }}
                  type="secondary"
                >
                  {metric.description}
                </Text>
              )}
            </div>
          </div>
          {!!metric?.analysis_dimension && (
            <Tag color="green" size="xsmall" style={{ height: 'fit-content' }}>
              {`AU = ${metric?.analysis_dimension}`}
            </Tag>
          )}
        </div>
      </Option>
    );
  });
};

function removeDefaultGuardrailMetrics(analysisConfig) {
  if (!analysisConfig?.guardrail_metrics) return [];

  const blacklistedGuardrailNames = BLACKLISTED_GUARDRAIL_METRICS.map(
    (metric) => metric.name,
  );

  return analysisConfig?.guardrail_metrics.filter(
    (metric: Metric) => !blacklistedGuardrailNames.includes(metric.name),
  );
}

interface MetricsGlossary {
  data: any[];
  errorMessage: string | null;
  is_dimension_filter_enabled?: boolean;
  is_deepdive_enabled?: boolean;
  is_triggered_analysis_enabled?: boolean;
}

const getInitialAnalysisConfig = (): ExperimentAnalysisConfig => ({
  primary_metrics: [],
  secondary_metrics: [],
  guardrail_metrics: [],
  control_metrics: [],
  deepdive_all_dimensions: false,
  enable_triggered_analysis: false,
  populationFilterEvent: '',
  populationFilterState: '',
});

interface MetricsProps {
  isAxaIncompatible?: boolean;
}

const Metrics: React.FC<MetricsProps> = ({ isAxaIncompatible = false }) => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);
  const editMode = isEditMode(action);

  const experimentContext = useExperimentContext();
  const experimentDispatch = useExperimentDispatchContext();
  const { analysis, currentExperiment, experiment, configurations } =
    experimentContext;
  const analysisConfig = useMemo<ExperimentAnalysisConfig>(() => {
    const contextAnalysisConfig = analysis?.analysisConfig;
    const guardrailMetrics = removeDefaultGuardrailMetrics(
      contextAnalysisConfig,
    );

    return {
      ...getInitialAnalysisConfig(),
      ...(contextAnalysisConfig || {}),
      primary_metrics: contextAnalysisConfig?.primary_metrics || [],
      secondary_metrics: contextAnalysisConfig?.secondary_metrics || [],
      guardrail_metrics: guardrailMetrics,
      control_metrics: contextAnalysisConfig?.control_metrics || [],
    };
  }, [analysis?.analysisConfig]);
  const config = analysisConfig;
  const experimentDomain = experiment?.domain;
  const strategyName = getAssignmentStrategy(configurations);
  const assignByFacet = getAssignByFacetFromDomain(experimentDomain)?.name;
  const currentAssignByFacet = getAssignByFacetFromDomain(
    currentExperiment?.domain,
  )?.name;
  const previousStrategyName = usePrevious(strategyName) || '';

  // Store raw analysis config to preserve deepdive_dimensions data
  const [deepdiveMessage, setDeepdiveMessage] = useState<ReactNode>(<></>);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [metricsGlossary, setMetricsGlossary] = useState<MetricsGlossary>({
    data: [],
    errorMessage: null,
    is_dimension_filter_enabled: false,
    is_deepdive_enabled: false,
    is_triggered_analysis_enabled: false,
  });
  const filterEnabled = useMemo(
    () => metricsGlossary.is_dimension_filter_enabled,
    [metricsGlossary.is_dimension_filter_enabled],
  );
  const [searchValue, setSearchValue] = useState<string>('');

  const debouncedSetSearchValue = useCallback(
    debounce((value: string) => {
      setSearchValue(value);
    }, METRIC_SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchValue.cancel();
    };
  }, [debouncedSetSearchValue]);

  const [strategyUnsupportedMessage, setStrategyUnsupportedMessage] = useState<
    string | null
  >(null);
  const [isPrimaryMetricOptedOut, setIsPrimaryMetricOptedOut] =
    useState<boolean>(false);
  const [primaryMetricOptOutReason, setPrimaryMetricOptOutReason] =
    useState<string>('');

  const updatePrimaryMetricOptOutMetadata = ({
    enabled,
    reason,
  }: {
    enabled: boolean;
    reason: string;
  }) => {
    if (!experiment) return;

    const nextExperiment = produce(experiment, (draft) => {
      if (!draft.metadata) {
        draft.metadata = {};
      }
      (draft.metadata as any).primaryMetricOptOutEnabled = enabled;
      (draft.metadata as any).primaryMetricOptOutReason = reason;
    });

    experimentDispatch(updateExperiment(nextExperiment));
  };

  const noMetricsAvailable = useMemo(() => {
    return (
      (readOnly &&
        !analysisConfig?.primary_metrics?.length &&
        !analysisConfig?.secondary_metrics?.length &&
        !analysisConfig?.guardrail_metrics?.length &&
        !analysisConfig?.control_metrics?.length) ||
      (!metricsGlossary.data.length && isCreateMode(action))
    );
  }, [analysisConfig, metricsGlossary.data, action]);

  const allSelectedMetricsArray = useMemo(
    () => [
      ...(analysisConfig?.primary_metrics || []),
      ...(analysisConfig?.secondary_metrics || []),
      ...(analysisConfig?.guardrail_metrics || []),
      ...(analysisConfig?.control_metrics || []),
    ],
    [analysisConfig],
  );

  const experimentStartMoreThan7Days = experiment?.startTime?.isBefore(
    dayjs().subtract(7, 'day').startOf('day'),
  );

  const showOverlay = (): string => {
    if (editMode) {
      if (experiment?.state === REQUESTED) return '';

      if (isAdmin()) return '';

      if (isEligibleForPostEndEditing(experiment)) {
        if (analysis?.schemaVersion !== 4)
          return 'This experiment does not support metric editing';
        return '';
      }

      if (![SCHEDULED, STARTED, RUNNING].includes(experiment?.state))
        return 'You can only edit the metric in scheduled, started, or running state';

      // Timeslice experiments have a 7-day threshold for metric editing
      if (isTimesliceExperiment(experiment) && experimentStartMoreThan7Days) {
        return 'You can only edit the metric within 7 days from experiment start for timeslice experiments';
      }

      if (analysis?.schemaVersion !== 4)
        return 'This experiment does not support metric editing';

      return '';
    }

    return '';
  };

  // TEMP: Remove once temporary timeslice controlled metrics editing is no longer needed.
  const isTimesliceControlMetricsEditable =
    editMode &&
    analysis?.schemaVersion === 4 &&
    isTimesliceEligibleForControlledMetricsEditing(experiment);
  const overlayMessage = showOverlay();
  const shouldLiftControlledMetrics =
    isTimesliceControlMetricsEditable && !!overlayMessage;

  const controlledMetricsWrapperStyle: React.CSSProperties | undefined =
    shouldLiftControlledMetrics
      ? {
          position: 'relative',
          zIndex: 2,
          isolation: 'isolate',
          display: 'flow-root',
          backgroundColor: '#fff',
          borderRadius: 8,
          marginBottom: 16,
          padding: '16px 0',
          pointerEvents: 'auto',
        }
      : undefined;

  // Add validation error tracking
  const hasValidationErrors = useMemo(() => {
    // Primary metric is mandatory unless opt-out is enabled.
    const hasPrimaryMetricSelection = analysisConfig?.primary_metrics?.some(
      (metric) => isValidMetric(metric),
    );
    const hasPrimaryMetricError =
      !readOnly && !isPrimaryMetricOptedOut && !hasPrimaryMetricSelection;
    const hasPrimaryMetricOptOutReasonError =
      !readOnly && isPrimaryMetricOptedOut && !primaryMetricOptOutReason.trim();

    // Check secondary metrics
    const hasSecondaryMetricError = analysisConfig?.secondary_metrics?.some(
      (metric) => !metric.name,
    );

    // Check guardrail metrics
    const hasGuardrailMetricError = analysisConfig?.guardrail_metrics?.some(
      (metric) => !metric.name,
    );

    // Check filter validation for all metrics
    const hasFilterError = allSelectedMetricsArray.some((metric) => {
      if (!metric.dimension_filters?.[0]?.dimension_filter_constraints)
        return false;

      return metric.dimension_filters[0].dimension_filter_constraints.some(
        (constraint) =>
          !constraint.dimension_name ||
          !constraint.operator ||
          isDimensionFilterValueEmpty(constraint.values) ||
          isFreeTextFilterPendingValidation(constraint),
      );
    });

    return (
      hasPrimaryMetricError ||
      hasPrimaryMetricOptOutReasonError ||
      hasSecondaryMetricError ||
      hasGuardrailMetricError ||
      hasFilterError
    );
  }, [
    allSelectedMetricsArray,
    isPrimaryMetricOptedOut,
    primaryMetricOptOutReason,
    readOnly,
  ]);

  useEffect(() => {
    experimentDispatch(updateValidMetric(!hasValidationErrors));
  }, [hasValidationErrors]);

  useEffect(() => {
    const optOutReason =
      (experiment?.metadata as any)?.primaryMetricOptOutReason || '';
    const optOutEnabled = (experiment?.metadata as any)
      ?.primaryMetricOptOutEnabled;
    setPrimaryMetricOptOutReason(optOutReason);
    setIsPrimaryMetricOptedOut(
      optOutEnabled === undefined ? !!optOutReason : !!optOutEnabled,
    );
  }, [experiment?.metadata]);

  // Auto-set/clear P0 opt-out based on AXA compatibility
  useEffect(() => {
    if (readOnly) return;

    if (isAxaIncompatible) {
      // Always set AXA incompatible reason - it takes precedence over any manual reason
      // since metrics selection is disabled for AXA incompatible configurations
      setIsPrimaryMetricOptedOut(true);
      setPrimaryMetricOptOutReason(AXA_INCOMPATIBLE_OPT_OUT_REASON);
      updatePrimaryMetricOptOutMetadata({
        enabled: true,
        reason: AXA_INCOMPATIBLE_OPT_OUT_REASON,
      });

      // Clear any existing metrics
      experimentDispatch(
        updateAnalysisConfig({
          ...(analysis?.analysisConfig || {}),
          primary_metrics: [],
          secondary_metrics: [],
          guardrail_metrics: [],
        }),
      );
    } else {
      // Clear opt-out if it was set due to AXA incompatibility
      if (primaryMetricOptOutReason === AXA_INCOMPATIBLE_OPT_OUT_REASON) {
        setIsPrimaryMetricOptedOut(false);
        setPrimaryMetricOptOutReason('');
        updatePrimaryMetricOptOutMetadata({
          enabled: false,
          reason: '',
        });
      }
    }
  }, [isAxaIncompatible, readOnly]);

  useEffect(() => {
    if (readOnly || !experiment) {
      return;
    }

    const hasSelectedPrimaryMetric = (
      analysisConfig?.primary_metrics || []
    ).some((metric) => isValidMetric(metric));

    if (!hasSelectedPrimaryMetric) {
      return;
    }

    const hasOptOutMetadata =
      !!(experiment?.metadata as any)?.primaryMetricOptOutEnabled ||
      !!(experiment?.metadata as any)?.primaryMetricOptOutReason;

    if (
      !hasOptOutMetadata &&
      !isPrimaryMetricOptedOut &&
      !primaryMetricOptOutReason
    ) {
      return;
    }

    setIsPrimaryMetricOptedOut(false);
    setPrimaryMetricOptOutReason('');

    const nextExperiment = produce(experiment, (draft) => {
      if (!draft.metadata) {
        draft.metadata = {};
      }
      (draft.metadata as any).primaryMetricOptOutEnabled = false;
      (draft.metadata as any).primaryMetricOptOutReason = '';
    });

    experimentDispatch(updateExperiment(nextExperiment));
  }, [
    analysisConfig?.primary_metrics,
    experiment,
    experimentDispatch,
    isPrimaryMetricOptedOut,
    primaryMetricOptOutReason,
    readOnly,
  ]);

  useEffect(() => {
    if (readOnly) {
      return;
    }

    if (analysisConfig?.primary_metrics?.length) {
      return;
    }

    experimentDispatch(
      updateAnalysisConfig({
        ...(analysis?.analysisConfig || {}),
        primary_metrics: [constructDimensionFields({ name: null })],
      }),
    );
  }, [
    analysis?.analysisConfig,
    analysisConfig?.primary_metrics?.length,
    experimentDispatch,
    readOnly,
  ]);

  async function fetchMetricsGlossary(params: { [key: string]: string }) {
    setIsLoading(true);
    // Clear stale timeSliceShuffle state from a previous strategy selection
    setStrategyUnsupportedMessage(null);

    try {
      const res = await Service.planner.fetchMetricsGlossary(params);

      const isDimensionFilterEnabled =
        res?.data?.is_dimension_filter_enabled || false;
      const isDeepDiveEnabled = res?.data?.is_deepdive_enabled || false;
      const isTriggeredAnalysisEnabled =
        res?.data?.is_triggered_analysis_enabled || false;

      setMetricsGlossary({
        data: res?.data?.metrics || [],
        errorMessage: null,
        is_dimension_filter_enabled: isDimensionFilterEnabled,
        is_deepdive_enabled: isDeepDiveEnabled,
        is_triggered_analysis_enabled: isTriggeredAnalysisEnabled,
      });

      if (!readOnly) {
        // Reverse auto opt-out if user switched to a supported strategy
        if (
          primaryMetricOptOutReason === METRICS_UNSUPPORTED_AUTO_OPT_OUT_REASON
        ) {
          setIsPrimaryMetricOptedOut(false);
          setPrimaryMetricOptOutReason('');
          updatePrimaryMetricOptOutMetadata({ enabled: false, reason: '' });
        }
      }

      if (isCreateMode(action)) {
        // Only set defaults in create mode; edit mode preserves existing values from the database
        experimentDispatch(
          updateAnalysisConfig({
            ...analysis?.analysisConfig,
            deepdive_all_dimensions: isDeepDiveEnabled
              ? DEFAULT_DEEP_DIVE_ALL_DIMENSIONS
              : false,
            enable_triggered_analysis: isTriggeredAnalysisEnabled
              ? DEFAULT_ENABLE_TRIGGERED_ANALYSIS
              : false,
          }),
        );
      }
    } catch (e) {
      if (e?.response?.status === HTTP_STATUS.METHOD_NOT_ALLOWED) {
        // Any 405 means metrics are unsupported for this strategy — auto opt-out instead of hard-blocking
        setIsPrimaryMetricOptedOut(true);
        setPrimaryMetricOptOutReason(METRICS_UNSUPPORTED_AUTO_OPT_OUT_REASON);
        updatePrimaryMetricOptOutMetadata({
          enabled: true,
          reason: METRICS_UNSUPPORTED_AUTO_OPT_OUT_REASON,
        });
        setStrategyUnsupportedMessage(
          e.response.data?.devMessage ||
            'AXA does not yet support metrics for this assignment strategy',
        );
        setMetricsGlossary({
          data: [],
          errorMessage: null,
          is_dimension_filter_enabled: false,
          is_deepdive_enabled: false,
          is_triggered_analysis_enabled: false,
        });
        return;
      }

      setMetricsGlossary({
        data: [],
        errorMessage: null,
        is_dimension_filter_enabled: false,
        is_deepdive_enabled: false,
        is_triggered_analysis_enabled: false,
      });
      notification.error({
        message: 'Failed to fetch metrics glossary',
        description: 'Please try again later.',
      });
    } finally {
      setIsLoading(false);
      setIsInitializing(false);
    }
  }

  useEffect(() => {
    const cleanup = () => {
      setMetricsGlossary({
        data: [],
        errorMessage: null,
        is_dimension_filter_enabled: false,
        is_deepdive_enabled: false,
        is_triggered_analysis_enabled: false,
      });
    };

    // In view mode, skip MIDAS API calls and only use analysis API data
    if (readOnly) {
      setMetricsGlossary({
        data: [],
        errorMessage: null,
        is_dimension_filter_enabled: false,
        is_deepdive_enabled: true,
        is_triggered_analysis_enabled: false,
      });
      setIsLoading(false);
      setIsInitializing(false);
      return;
    }

    // Handle strategy and facet changes with metric compatibility checking
    const handleStrategyChange = async () => {
      if (currentAssignByFacet && currentAssignByFacet !== assignByFacet) {
        experimentDispatch(updateAnalysisConfig(getInitialAnalysisConfig()));
        return;
      }

      // Only check for strategy changes if we have a previous strategy (skip initial render)
      if (previousStrategyName && previousStrategyName !== strategyName) {
        if (isCompatibleStrategy(previousStrategyName, strategyName)) {
          return;
        }

        const hasMetrics =
          analysisConfig?.primary_metrics?.some((m) => m.name) ||
          analysisConfig?.secondary_metrics?.some((m) => m.name) ||
          analysisConfig?.guardrail_metrics?.some((m) => m.name);

        if (!hasMetrics) {
          return;
        }

        try {
          const [primaryResults, secondaryResults, guardrailResults] =
            await Promise.all([
              filterCompatibleMetrics(
                analysisConfig?.primary_metrics || [],
                assignByFacet,
                strategyName,
              ),
              filterCompatibleMetrics(
                analysisConfig?.secondary_metrics || [],
                assignByFacet,
                strategyName,
              ),
              filterCompatibleMetrics(
                analysisConfig?.guardrail_metrics || [],
                assignByFacet,
                strategyName,
              ),
            ]);

          experimentDispatch(
            updateAnalysisConfig({
              ...analysis?.analysisConfig,
              primary_metrics: primaryResults.compatible,
              secondary_metrics: secondaryResults.compatible,
              guardrail_metrics: guardrailResults.compatible,
            }),
          );

          const totalIncompatible = [
            ...primaryResults.incompatible,
            ...secondaryResults.incompatible,
            ...guardrailResults.incompatible,
          ].filter((m) => m.name).length;

          const totalCompatible = [
            ...primaryResults.compatible,
            ...secondaryResults.compatible,
            ...guardrailResults.compatible,
          ].filter((m) => m.name).length;

          if (totalIncompatible > 0 && totalCompatible > 0) {
            notification.info({
              message: 'Assignment Strategy Changed',
              description: `${totalIncompatible} metric(s) removed due to incompatibility. ${totalCompatible} compatible metric(s) preserved.`,
              duration: 5,
            });
          } else if (totalIncompatible > 0 && totalCompatible === 0) {
            notification.warning({
              message: 'Assignment Strategy Changed',
              description:
                'All metrics were removed as they are not compatible with the new assignment strategy.',
              duration: 5,
            });
          } else if (totalIncompatible === 0 && totalCompatible > 0) {
            notification.success({
              message: 'Assignment Strategy Changed',
              description:
                'All existing metrics are compatible with the new assignment strategy.',
              duration: 3,
            });
          }
        } catch (error) {
          logger.error(
            error,
            'Error checking metric compatibility during strategy change',
            {
              previousStrategy: previousStrategyName,
              newStrategy: strategyName,
              assignByFacet,
            },
          );
        }
      }
    };

    handleStrategyChange();

    if (!assignByFacet || !strategyName) {
      return cleanup;
    }

    let params = {
      assign_by: assignByFacet,
      strategy: strategyName,
    };

    if (searchValue != '') {
      params = Object.assign(params, {
        owner: searchValue,
        description: searchValue,
        display_name: searchValue,
      });
    }

    fetchMetricsGlossary(params);
  }, [assignByFacet, searchValue, strategyName, action]);

  useEffect(() => {
    if (!analysisConfig?.deepdive_all_dimensions) {
      setDeepdiveMessage(<></>);
      return;
    }

    const generateMessage = async () => {
      const nonEmptyMetrics = allSelectedMetricsArray.filter((m) => !!m.name);
      if (
        nonEmptyMetrics.length > 0 &&
        analysisConfig?.deepdive_all_dimensions
      ) {
        const message = await generateMetricMessage(
          analysisConfig?.deepdive_all_dimensions,
          strategyName,
          analysisConfig as Parameters<typeof generateMetricMessage>[2],
          readOnly,
          analysis?.analysisConfig,
        );
        setDeepdiveMessage(message);
      } else {
        // Reset the message when there are no metrics
        setDeepdiveMessage(<></>);
      }
    };

    generateMessage();
  }, [
    allSelectedMetricsArray,
    analysisConfig?.deepdive_all_dimensions,
    strategyName,
    readOnly,
    analysis?.analysisConfig,
  ]);

  const handleAddPrimaryMetric = () => {
    const nextAnalysisConfig = produce(analysis?.analysisConfig, (draft) => {
      if (!draft.primary_metrics) {
        draft.primary_metrics = [];
      }

      draft.primary_metrics.push(
        constructDimensionFields({
          name: null,
        }),
      );
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handleAddSecondaryMetric = () => {
    const secondaryMetrics = config?.secondary_metrics;

    const unpopulatedMetricRowExists = secondaryMetrics.some(
      (m) => m.name === null,
    );

    if (unpopulatedMetricRowExists) {
      return;
    }

    const nextAnalysisConfig = produce(config, (draft) => {
      if (!draft.secondary_metrics) {
        draft.secondary_metrics = [];
      }

      draft.secondary_metrics.push(
        constructDimensionFields({
          name: null,
        }),
      );
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handleAddGuardrailMetric = () => {
    const guardrailMetrics = analysisConfig.guardrail_metrics;

    const unpopulatedMetricRowExists = guardrailMetrics.some(
      (m) => m.name === null,
    );

    if (unpopulatedMetricRowExists) {
      return;
    }

    const nextAnalysisConfig = produce(config, (draft) => {
      if (!draft.guardrail_metrics) {
        draft.guardrail_metrics = [];
      }

      draft.guardrail_metrics.push(
        constructDimensionFields({
          name: null,
        }),
      );
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handlePrimaryMetricsDeletion = () => {
    const nextAnalysisConfig = produce(config, (draft) => {
      draft.primary_metrics = [];
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handleSecondaryMetricsDeletion = (index: number) => {
    const filteredMetrics = analysisConfig.secondary_metrics.filter(
      (_, i) => i !== index,
    );
    const nextAnalysisConfig = produce(config, (draft) => {
      draft.secondary_metrics = filteredMetrics;
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handleGuardrailMetricsDeletion = (index: number) => {
    const filteredMetrics = analysisConfig.guardrail_metrics.filter(
      (_, i) => i !== index,
    );
    const nextAnalysisConfig = produce(config, (draft) => {
      draft.guardrail_metrics = filteredMetrics;
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handleControlMetricsChange = (
    controlMetrics: ControlMetricWithFilters[],
  ) => {
    const nextAnalysisConfig = produce(config, (draft) => {
      draft.control_metrics = controlMetrics;
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  // Check if this is a timeslice experiment (for showing timeslice-specific UI)
  const showTimesliceFeatures = isTimeSliceStrategy(strategyName);

  // Track previous constraints to detect changes
  const prevConstraintsRef = React.useRef<string>('');
  // Track config version to detect stale closures
  const configVersionRef = React.useRef<number>(0);

  // Apply population filters to metrics that need them
  useEffect(() => {
    if (readOnly || !experiment?.constraints?.length) return;

    const currentConstraintsKey = JSON.stringify(experiment.constraints);
    const constraintsChanged =
      prevConstraintsRef.current !== '' &&
      prevConstraintsRef.current !== currentConstraintsKey;

    // Increment version to track this effect invocation
    const effectVersion = ++configVersionRef.current;

    const applyFiltersToAllMetrics = async () => {
      // Process all metric types with a single batch API call
      const result = await processAllMetricTypesForPopulationFilters(
        {
          primary_metrics: config?.primary_metrics || [],
          secondary_metrics: config?.secondary_metrics || [],
          guardrail_metrics: config?.guardrail_metrics || [],
          control_metrics: config?.control_metrics || [],
        },
        experiment.constraints,
        constraintsChanged,
      );

      // Check if config has been updated by a newer effect invocation
      // This prevents overwriting user changes made while API was pending
      if (configVersionRef.current !== effectVersion) {
        return;
      }

      if (result.anyChanges) {
        const nextConfig = produce(config, (draft) => {
          draft.primary_metrics = result.primary_metrics;
          draft.secondary_metrics = result.secondary_metrics;
          draft.guardrail_metrics = result.guardrail_metrics;
          draft.control_metrics =
            result.control_metrics as ControlMetricWithFilters[];
        });
        experimentDispatch(updateAnalysisConfig(nextConfig));
      }
    };

    applyFiltersToAllMetrics();
    prevConstraintsRef.current = currentConstraintsKey;
  }, [
    // Trigger when metric names change (includes control_metrics)
    analysisConfig?.primary_metrics?.map((m) => m.name).join(','),
    analysisConfig?.secondary_metrics?.map((m) => m.name).join(','),
    analysisConfig?.guardrail_metrics?.map((m) => m.name).join(','),
    analysisConfig?.control_metrics
      ?.map((m) => m.candidate_id || m.name)
      .join(','),
    JSON.stringify(experiment?.constraints),
    readOnly,
  ]);

  const collapseProps = {
    title: (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography.Text>Metrics</Typography.Text>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              fontSize: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <span style={{ fontWeight: 500 }}>
                Select metrics to measure the impact of your experiment.
              </span>

              <Typography.Link
                href={MIDAS_METRICS_ENDPOINT}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: 12,
                }}
              >
                Explore Midas to discover available metrics.
              </Typography.Link>
            </div>
            <span style={{ fontWeight: 500 }}>
              Population constraints (country, city and vehicle type) will be
              automatically applied for your selected metrics.
            </span>
          </div>
        </div>
      </div>
    ),
    // title: 'Metrics',
    defaultOpen: true,
  };

  if (!readOnly && strategyUnsupportedMessage) {
    return (
      <SectionCollapse {...collapseProps}>
        <MetricsWrapper>
          <Alert
            message={strategyUnsupportedMessage}
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
        </MetricsWrapper>
      </SectionCollapse>
    );
  }

  if (!readOnly && !!metricsGlossary.errorMessage) {
    return (
      <SectionCollapse {...collapseProps}>
        <MetricsUnsupported errorMessage={metricsGlossary.errorMessage} />
      </SectionCollapse>
    );
  }

  // Show alert and hide metrics selection for AXA incompatible timeslice experiments
  if (!readOnly && isAxaIncompatible) {
    return (
      <SectionCollapse {...collapseProps}>
        <MetricsUnsupported errorMessage="This experiment uses a timeslice configuration that is not compatible with AXA Scorecards. Metrics selection is not available for non-hourly start times or non-hourly slice windows." />
      </SectionCollapse>
    );
  }

  if (noMetricsAvailable && !isLoading && !searchValue) {
    return (
      <SectionCollapse {...collapseProps}>
        <MetricsWrapper>
          <img
            src="/file.svg"
            width={130}
            height={60}
            style={{ marginTop: 40, marginBottom: 40 }}
          />
          <Text strong style={{ margin: 0, fontSize: 24 }}>
            No metrics available!
          </Text>
          <Text style={{ color: '#707070' }}>
            This experiment does not have any metrics yet
          </Text>
        </MetricsWrapper>
      </SectionCollapse>
    );
  }

  return (
    <Card
      styles={{
        body: {
          padding: 0,
        },
      }}
    >
      <Overlay
        subTitle={overlayMessage}
        showOverlay={!!overlayMessage}
        messagePosition={shouldLiftControlledMetrics ? 'top' : 'center'}
        zIndex={1}
      />
      <SectionCollapse {...collapseProps}>
        <MetricsWrapper className="metrics-wrapper">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong>Primary Metric (P0)</Text>
            <Tag
              size="xsmall"
              color={isPrimaryMetricOptedOut ? 'orange' : 'red'}
            >
              {isPrimaryMetricOptedOut ? 'Opted Out' : 'Required'}
            </Tag>
          </div>
          {readOnly && !analysisConfig?.primary_metrics?.length ? (
            <div>No selected metrics</div>
          ) : (
            <>
              <MetricRow
                filterEnabled={filterEnabled}
                strategy={strategyName}
                metricsGlossaryData={metricsGlossary.data || []}
                metrics={analysisConfig}
                metricType="primary"
                metricIndex={0}
                options={getOptions(
                  metricsGlossary.data,
                  analysisConfig.primary_metrics,
                )}
                onSearch={debouncedSetSearchValue}
                onDelete={handlePrimaryMetricsDeletion}
                showValidation={hasValidationErrors && !isPrimaryMetricOptedOut}
                prefix="P0"
              />
              {analysisConfig?.primary_metrics?.[0] &&
                renderPopulationFilterBadges(analysisConfig.primary_metrics[0])}
            </>
          )}
          {!readOnly && !analysisConfig?.primary_metrics?.length && (
            <Button
              style={{
                minWidth: '100%',
                minHeight: 32,
                background: '#F5F5F5',
                border: 'none',
              }}
              icon={<AddUniversal />}
              onClick={handleAddPrimaryMetric}
            />
          )}
          {!readOnly && (
            <div style={{ marginTop: 8 }}>
              <Checkbox
                checked={isPrimaryMetricOptedOut}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setIsPrimaryMetricOptedOut(checked);

                  if (!checked) {
                    setPrimaryMetricOptOutReason('');
                    updatePrimaryMetricOptOutMetadata({
                      enabled: false,
                      reason: '',
                    });
                    return;
                  }

                  experimentDispatch(
                    updateAnalysisConfig({
                      ...(analysis?.analysisConfig || {}),
                      primary_metrics: [
                        constructDimensionFields({
                          name: null,
                        }),
                      ],
                    }),
                  );

                  updatePrimaryMetricOptOutMetadata({
                    enabled: true,
                    reason: primaryMetricOptOutReason,
                  });
                }}
              >
                Skipping primary metric? Help us understand why.
              </Checkbox>
            </div>
          )}
          {isPrimaryMetricOptedOut && (
            <>
              <Input.TextArea
                rows={4}
                value={primaryMetricOptOutReason}
                onChange={(event) => {
                  const value = event.target.value;
                  setPrimaryMetricOptOutReason(value);
                  updatePrimaryMetricOptOutMetadata({
                    enabled: true,
                    reason: value,
                  });
                }}
                placeholder="Enter your reason"
                style={{ marginTop: 8 }}
                disabled={readOnly}
              />
              {hasValidationErrors &&
                isPrimaryMetricOptedOut &&
                !primaryMetricOptOutReason.trim() && (
                  <Typography.Text type="danger">
                    Please provide opt out reason
                  </Typography.Text>
                )}
            </>
          )}
        </MetricsWrapper>

        <MetricsWrapper>
          <Text strong>Secondary Metrics (P1)</Text>
          {readOnly && !analysisConfig?.secondary_metrics?.length ? (
            <div>No selected metrics</div>
          ) : (
            analysisConfig?.secondary_metrics?.map((metric, index) => (
              <React.Fragment key={`secondary-${metric?.name}-${index}`}>
                <MetricRow
                  filterEnabled={filterEnabled}
                  strategy={strategyName}
                  metricsGlossaryData={metricsGlossary.data || []}
                  metrics={analysisConfig}
                  metricType="secondary"
                  metricIndex={index}
                  options={getOptions(
                    metricsGlossary.data,
                    analysisConfig?.secondary_metrics || [],
                  )}
                  onSearch={debouncedSetSearchValue}
                  onDelete={() => handleSecondaryMetricsDeletion(index)}
                  showValidation={hasValidationErrors}
                  prefix="P1"
                />
                {renderPopulationFilterBadges(metric)}
                {hasValidationErrors && !metric.name && (
                  <Typography.Text type="danger">
                    Please select a metric
                  </Typography.Text>
                )}
              </React.Fragment>
            ))
          )}
          {!readOnly &&
            (analysisConfig?.secondary_metrics?.length >=
            MAX_SECONDARY_METRICS ? (
              <Tooltip
                title={errorLimitMsg('Secondary', MAX_SECONDARY_METRICS)}
              >
                <Button
                  style={{
                    minWidth: '100%',
                    minHeight: 32,
                    background: '#F5F5F5',
                    border: 'none',
                  }}
                  icon={<AddUniversal />}
                  disabled
                />
              </Tooltip>
            ) : (
              <Button
                style={{
                  minWidth: '100%',
                  minHeight: 32,
                  background: '#F5F5F5',
                  border: 'none',
                }}
                icon={<AddUniversal />}
                onClick={handleAddSecondaryMetric}
              />
            ))}
        </MetricsWrapper>

        <MetricsWrapper style={{ paddingBottom: 0 }}>
          <Text strong>Guardrail Metrics (G)</Text>
          {readOnly && !analysisConfig?.guardrail_metrics?.length ? (
            <div>No selected metrics</div>
          ) : (
            analysisConfig?.guardrail_metrics?.map((metric, index) => (
              <React.Fragment key={`guardrail-${metric?.name}-${index}`}>
                <MetricRow
                  filterEnabled={filterEnabled}
                  strategy={strategyName}
                  metricsGlossaryData={metricsGlossary.data || []}
                  metrics={analysisConfig}
                  metricType="guardrail"
                  metricIndex={index}
                  options={getOptions(
                    metricsGlossary.data,
                    analysisConfig?.guardrail_metrics || [],
                  )}
                  onSearch={debouncedSetSearchValue}
                  onDelete={() => handleGuardrailMetricsDeletion(index)}
                  showValidation={hasValidationErrors}
                  prefix="G"
                />
                {renderPopulationFilterBadges(metric)}
                {hasValidationErrors && !metric.name && (
                  <Typography.Text type="danger">
                    Please select a metric
                  </Typography.Text>
                )}
              </React.Fragment>
            ))
          )}
          {!readOnly &&
            (analysisConfig?.guardrail_metrics?.length >=
            MAX_GUARDRAIL_METRICS ? (
              <Tooltip
                title={errorLimitMsg('Guardrail', MAX_GUARDRAIL_METRICS)}
              >
                <Button
                  style={{
                    minWidth: '100%',
                    minHeight: 32,
                    background: '#F5F5F5',
                    border: 'none',
                  }}
                  icon={<AddUniversal />}
                  disabled
                />
              </Tooltip>
            ) : (
              <Button
                style={{
                  minWidth: '100%',
                  minHeight: 32,
                  background: '#F5F5F5',
                  border: 'none',
                }}
                icon={<AddUniversal />}
                onClick={handleAddGuardrailMetric}
              />
            ))}
        </MetricsWrapper>

        {isInitializing ? (
          <Flex justify="center" align="center" style={{ padding: 24 }}>
            <Spin />
          </Flex>
        ) : (
          <>
            {(metricsGlossary.is_deepdive_enabled ||
              metricsGlossary.is_triggered_analysis_enabled) && <Divider />}
            <Flex vertical gap={16}>
              {metricsGlossary.is_deepdive_enabled && (
                <Alert
                  style={{ borderColor: '#dbdbdb' }}
                  message={<Typography.Text>Enable Deep Dive</Typography.Text>}
                  description={
                    analysisConfig?.deepdive_all_dimensions ? (
                      <Typography.Metadata>
                        {deepdiveMessage}
                      </Typography.Metadata>
                    ) : (
                      <></>
                    )
                  }
                  type="neutral"
                  action={
                    <Switch
                      checked={analysisConfig?.deepdive_all_dimensions}
                      onChange={(checked) => {
                        experimentDispatch(
                          updateAnalysisConfig({
                            ...analysis?.analysisConfig,
                            deepdive_all_dimensions: checked,
                          }),
                        );
                      }}
                      disabled={readOnly}
                    />
                  }
                />
              )}

              {metricsGlossary.is_triggered_analysis_enabled && (
                <Alert
                  style={{ borderColor: '#dbdbdb' }}
                  message={<Typography.Text>Trigger Analysis</Typography.Text>}
                  description={
                    <Typography.Metadata>
                      Enable this to analyse results only for users who
                      triggered your experiment variable.
                    </Typography.Metadata>
                  }
                  type="neutral"
                  action={
                    <Switch
                      checked={analysisConfig?.enable_triggered_analysis}
                      onChange={(checked) => {
                        experimentDispatch(
                          updateAnalysisConfig({
                            ...analysis?.analysisConfig,
                            enable_triggered_analysis: checked,
                          }),
                        );
                      }}
                      disabled={readOnly}
                    />
                  }
                />
              )}
            </Flex>
          </>
        )}

        {/* Controlled Metrics Section for timeslice experiments */}
        {/* TEMP: Remove once temporary timeslice controlled metrics editing is no longer needed. */}
        {/* When the carveout is active, zIndex:2 lifts it above the local overlay (zIndex:1). */}
        {showTimesliceFeatures && (
          <div
            data-testid="controlled-metrics-wrapper"
            style={controlledMetricsWrapperStyle}
          >
            <ControlledMetricsSection
              controlMetrics={analysisConfig?.control_metrics || []}
              strategy={strategyName}
              filterEnabled={filterEnabled}
              onChange={handleControlMetricsChange}
              onSearch={debouncedSetSearchValue}
              disabled={readOnly && !isTimesliceControlMetricsEditable}
              showValidation={hasValidationErrors}
            />
          </div>
        )}
      </SectionCollapse>
    </Card>
  );
};

export function MetricsUnsupported({ errorMessage }: { errorMessage: string }) {
  return (
    <MetricsWrapper>
      <img
        src="/file.svg"
        width={130}
        height={60}
        style={{ marginTop: 40, marginBottom: 40 }}
      />
      <Text strong style={{ margin: 0, fontSize: 24 }}>
        Metrics are not supported!
      </Text>
      <Text style={{ color: '#707070' }}>
        {errorMessage}
        <br />
        For more information, please contact us and submit a feature request.
      </Text>
      <div style={{ paddingTop: 20 }}>
        <Button
          onClick={(_) => {
            window.open(GRABX_SLACK_CHANNEL);
          }}
          type="primary"
          size="small"
          style={{ width: 222, marginRight: 8 }}
        >
          Connect to the GrabX team
        </Button>
        <Button
          onClick={(_e) => {
            window.open(GRABX_SLACK_CHANNEL);
          }}
          type="secondary"
          size="small"
          style={{ width: 212 }}
        >
          Submit a feature request
        </Button>
      </div>
    </MetricsWrapper>
  );
}

// Helper functions for assignment strategy compatibility checking
async function checkMetricCompatibility(
  metricName: string,
  assignByFacet: string,
  newStrategy: string,
): Promise<boolean> {
  try {
    const params = {
      assign_by: assignByFacet,
      strategy: newStrategy,
    };

    const res = await Service.planner.fetchMetricsGlossary(params);
    const availableMetrics = res?.data?.metrics || [];

    // Check if the metric exists in the available metrics for the new strategy
    return availableMetrics.some((metric: any) => metric.name === metricName);
  } catch (error) {
    logger.error(error, 'Error checking metric compatibility with strategy', {
      metricName,
      assignByFacet,
      newStrategy,
    });
    return false;
  }
}

async function filterCompatibleMetrics(
  metrics: MetricWithDimensionFields[],
  assignByFacet: string,
  newStrategy: string,
): Promise<{
  compatible: MetricWithDimensionFields[];
  incompatible: MetricWithDimensionFields[];
}> {
  const compatible: MetricWithDimensionFields[] = [];
  const incompatible: MetricWithDimensionFields[] = [];

  for (const metric of metrics) {
    if (!metric.name) {
      // Keep empty metric slots
      compatible.push(metric);
      continue;
    }

    const isCompatible = await checkMetricCompatibility(
      metric.name,
      assignByFacet,
      newStrategy,
    );

    if (isCompatible) {
      compatible.push(metric);
    } else {
      incompatible.push(metric);
    }
  }

  return { compatible, incompatible };
}

export { MetricTag } from './index.styled';

export default Metrics;
